import { Mesh, VertexBuffer, Vector3 } from 'babylonjs';
import type { BaseTexture } from 'babylonjs';
import type { MeshSnapshot } from '../solver/types';

type TexturedMaterial = {
  albedoTexture?: BaseTexture | null;
  diffuseTexture?: BaseTexture | null;
  emissiveTexture?: BaseTexture | null;
};

/**
 * The Babylon → pure-snapshot adapter: read a mesh's vertex data, bake its world
 * matrix into the positions (so everything downstream is in world space), and read
 * its albedo/diffuse texture dimensions for texel-density. This is the one file
 * that bridges Babylon to the engine-free `solver/`; it lives in `app/` so `solver/`
 * stays import-clean. Runs on the main thread; the resulting plain typed arrays are
 * then transferred to the worker.
 */
export function extractMeshSnapshot(mesh: Mesh): MeshSnapshot {
  const local = mesh.getVerticesData(VertexBuffer.PositionKind);
  const rawIndices = mesh.getIndices();
  if (!local || !rawIndices) {
    return { positions: new Float32Array(0), indices: new Uint32Array(0), uvs: null, textureSize: null };
  }

  const world = mesh.computeWorldMatrix(true);
  const positions = new Float32Array(local.length);
  const tmp = new Vector3();
  for (let i = 0; i < local.length / 3; i++) {
    Vector3.TransformCoordinatesFromFloatsToRef(local[i * 3], local[i * 3 + 1], local[i * 3 + 2], world, tmp);
    positions[i * 3] = tmp.x;
    positions[i * 3 + 1] = tmp.y;
    positions[i * 3 + 2] = tmp.z;
  }

  const indices = rawIndices instanceof Uint32Array ? rawIndices.slice() : Uint32Array.from(rawIndices);
  const uvData = mesh.getVerticesData(VertexBuffer.UVKind);
  const uvs = uvData ? Float32Array.from(uvData) : null;

  return { positions, indices, uvs, textureSize: readTextureSize(mesh) };
}

function readTextureSize(mesh: Mesh): { width: number; height: number } | null {
  const mat = mesh.material as unknown as TexturedMaterial | null;
  if (!mat) return null;
  const tex: BaseTexture | null | undefined = mat.albedoTexture ?? mat.diffuseTexture ?? mat.emissiveTexture;
  if (!tex) return null;
  const size = tex.getSize();
  return size && size.width > 0 ? { width: size.width, height: size.height } : null;
}
